"use client";
import { useCallback, useEffect, useState } from "react";
import { useScopedI18n } from "@/messages/client";
import Title from "@/components/ui/title";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { DirectusImage } from "@/components/DirectusImage";
import RouterLink from "@/components/ui/router-link";
import { ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import "./index.css";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import useEmblaCarousel from "embla-carousel-react";
import { useLang } from "@/hooks/useSiteInfo";
import useLangData from "@/hooks/useLangData";

interface ApplicationCategoryData {
  name?: string;
  summary?: string;
  banner?: string;
  lang_data?: {
    summary?: string;
    lang: string;
    name?: string;
    feature_data?: {
      text?: string;
    }[];
  }[];
  feature_data?: {
    text: string;
  }[];
  applications?: {
    slug?: string;
    name?: string;
    icon?: string;
  }[];
}

export default function ApplicationOverviewClient(props: {
  data?: ApplicationCategoryData[];
}) {
  const t = useScopedI18n("应用");

  const getLangData = useLangData();
  const lang = useLang();
  const [isMobile, setIsMobile] = useState<boolean>();
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);

  const [thumbsRef, thumbApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    loop: true,

    breakpoints: {
      "(min-width: 768px)": {
        loop: true,
        dragFree: true,
        containScroll: "keepSnaps",
      },
    },
  });

  const onSelect = useCallback(() => {
    if (!mainApi || !thumbApi) {
      return;
    }

    setCurrent(mainApi.selectedScrollSnap() || 0);
    thumbApi?.scrollTo(mainApi.selectedScrollSnap() || 0);
  }, [mainApi, thumbApi]);

  const getLangFeatureData = useCallback(
    (el: ApplicationCategoryData) => {
      return (
        el.lang_data?.find((item) => Number(item.lang) === lang)
          ?.feature_data || el?.feature_data
      );
    },
    [lang]
  );

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  useEffect(() => {
    const listener = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
    };
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, []);

  return (
    <div className="w-full">
      <Title underline={false} className="font-medium" title={t("浏览应用")} />
      {isMobile && (
        <Accordion
          type="single"
          collapsible
          className="mt-5 flex flex-col gap-y-3">
          {props.data?.map((el, index) => (
            <AccordionItem
              className="border-none"
              key={index}
              value={`category_${index}`}>
              <AccordionTrigger className="flex items-center justify-between rounded-lg bg-zinc-50 pl-10 pr-4 text-sm">
                {getLangData("name", el.name ?? "", el.lang_data)}
              </AccordionTrigger>
              <AccordionContent className="bg-zinc-50 px-4 pb-6 pt-2">
                <div className="relative w-full">
                  <DirectusImage
                    id={el.banner || ""}
                    alt={el.name || ""}
                    width={660}
                    height={338}
                    className="w-full rounded-md"
                  />
                  <div className="absolute top-0 z-10 h-full px-6 py-5 text-sm text-white">
                    <p className="line-clamp-6">
                      {getLangData("summary", el.summary ?? "", el.lang_data)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-center text-primary">
                  <img
                    className="mr-1 h-4 w-4"
                    src="/icon/advantage.svg"
                    alt="Icon Advantage"
                  />
                  <span className="text-sm">{t("POWERQUARK优势")}</span>
                </div>
                <ul className="mt-1.5 flex flex-col gap-y-2 pl-6">
                  {getLangFeatureData(el)?.map((item, i) => (
                    <li
                      key={`advantage_${index}_${i}`}
                      className="flex text-xs leading-none">
                      <span className="mr-2 h-3 border-l-2 border-l-primary"></span>
                      {item.text}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center text-primary">
                  <img
                    className="mr-1 h-4 w-4"
                    src="/icon/application.svg"
                    alt="Icon Application"
                  />
                  <span className="text-sm">{t("更多应用")}</span>
                </div>
                <div className="mt-3 flex flex-col gap-y-3">
                  {el.applications?.map((item, i) => (
                    <RouterLink
                      key={`application_${index}_${i}`}
                      href={item.slug ? `/application/${item.slug}` : undefined}
                      className="flex items-center justify-center rounded-lg bg-white py-2 text-sm">
                      {item.icon && (
                        <DirectusImage
                          id={item.icon}
                          alt={item.name || ""}
                          width={30}
                          height={30}
                          className="mr-1 h-5 w-auto flex-shrink-0"
                        />
                      )}
                      <div className="line-clamp-1">{item.name}</div>
                      <ChevronsRight
                        className="ml-7 flex-shrink-0 text-primary"
                        size={18}
                      />
                    </RouterLink>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
      {!isMobile && (
        <>
          <div className="relative w-full px-[72px] xl:-ml-[72px] xl:box-content">
            <div className="mt-14 w-full">
              <div className="relative overflow-hidden" ref={thumbsRef}>
                <div className="flex gap-x-0.5">
                  {props.data?.map((el, index) => (
                    <div
                      key={index}
                      className={cn(
                        "application-tab w-1/3 shrink-0 py-3 lg:w-1/4",
                        {
                          "application-tab-active": index === current,
                        }
                      )}
                      onClick={() => {
                        mainApi?.scrollTo(index);
                      }}>
                      {getLangData("name", el.name ?? "", el.lang_data)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <span
              className="application-prev"
              onClick={() => {
                mainApi?.scrollPrev();
              }}>
              <ChevronLeft size={25} />
            </span>
            <span
              className="application-next"
              onClick={() => {
                mainApi?.scrollNext();
              }}>
              <ChevronRight size={25} />
            </span>
          </div>
          <Carousel
            className="mt-24 w-full"
            opts={{ loop: true, duration: 0, watchDrag: false }}
            setApi={setMainApi}>
            <CarouselContent className="m-0">
              {props.data?.map((el, index) => (
                <CarouselItem className="basis-full p-0" key={index}>
                  <h3 className="inline-block border-b-2 border-b-primary pb-1 text-4xl">
                    {getLangData("name", el.name ?? "", el.lang_data)}
                  </h3>
                  <div className="flex gap-x-24">
                    <div className="shrink-0 md:w-[380px] lg:w-[440px] xl:w-[500px]">
                      <p className="mt-9 whitespace-pre-line text-base">
                        {getLangData("summary", el.summary ?? "", el.lang_data)}
                      </p>
                      <div className="mt-12 flex items-center text-primary">
                        <img
                          className="mr-2 h-6 w-6"
                          src="/icon/advantage.svg"
                          alt="Icon Advantage"
                        />
                        <span className="text-2xl font-bold">
                          {t("POWERQUARK优势")}
                        </span>
                      </div>
                      <ul className="mt-4 flex flex-wrap gap-y-4 pl-8">
                        {getLangFeatureData(el)?.map((item, i) => (
                          <li
                            key={`advantage_${index}_${i}`}
                            className="flex min-w-[50%] flex-auto">
                            <span className="mr-2 mt-1 h-4 border-l-2 border-l-primary"></span>
                            {item.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex grow basis-0 items-center justify-center overflow-hidden rounded-md">
                      <DirectusImage
                        id={el.banner || ""}
                        alt={el.name || ""}
                        width={660}
                        height={338}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="mt-12 flex items-start">
                    <div className="flex shrink-0 items-center text-primary">
                      <img
                        className="mr-2 h-6 w-6"
                        src="/icon/application.svg"
                        alt="Icon Application"
                      />
                      <span className="text-2xl font-bold">
                        {t("更多应用")}
                      </span>
                    </div>
                    <div className="ml-12 grow pt-4">
                      <hr className="w-full border-t-primary" />
                      <div className="mt-5 grid grid-cols-3 items-start gap-x-[80px] gap-y-4 py-2">
                        {el.applications?.map((item, i) => (
                          <RouterLink
                            key={`application_${index}_${i}`}
                            href={
                              item.slug
                                ? `/application/${item.slug}`
                                : undefined
                            }
                            className="flex items-center justify-center rounded-lg border border-zinc-100 py-4 text-lg hover:shadow-md en:text-sm px-4">
                            {item.icon && (
                              <DirectusImage
                                id={item.icon}
                                alt=""
                                width={30}
                                height={30}
                                className="mr-2 h-[30px] w-auto flex-shrink-0"
                              />
                            )}
                            <div className="line-clamp-1">{item.name}</div>
                            <ChevronsRight
                              className="ml-10 flex-shrink-0 text-primary en:ml-6"
                              size={24}
                            />
                          </RouterLink>
                        ))}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </>
      )}
    </div>
  );
}
